import { skipToken } from '@reduxjs/toolkit/query';

import { useAppSelector } from '@verantos-dev/core-annotation-tool';

import { useIsAgentStepEnabled } from './useCreateProjectWizardSteps';
import {
  useGetAnnotationAgentAllocationPreviewQuery,
  useGetAnnotationAgentSetupQuery,
} from '../api/createProjectApiSlice';
import { selectAnnotationAgentEnabled, selectWorkflowId } from '../state/selector';
import type { AnnotationAgentAllocationPreviewRequest } from '../types/request';
import type { AnnotationAgentAllocationPreviewResponse } from '../types/response';

// TODO: cleanup with cp_annotation_createproject_agent ff

/**
 * Whether the annotation agent takes part in the project: its step is in this wizard session
 * and its toggle is on. Always false with the flag off.
 */
export const useIsAnnotationAgentOn = (): boolean => {
  const isAgentStepEnabled = useIsAgentStepEnabled();
  const isToggleOn = useAppSelector(selectAnnotationAgentEnabled);
  return isAgentStepEnabled && isToggleOn;
};

/**
 * The agent account's user ID while the agent is on, from the setup query step 5 already made;
 * null while the agent is off or the setup has no agent. Nothing is requested while it is off.
 */
export const useAnnotationAgentAccountId = (isAgentOn: boolean): string | null => {
  const { data } = useGetAnnotationAgentSetupQuery(undefined, { skip: !isAgentOn });
  return isAgentOn ? data?.data.agent?.id ?? null : null;
};

export type AgentAllocationPreviewStatus = 'loading' | 'error' | 'ready';

interface UseAnnotationAgentAllocationPreviewResult {
  status: AgentAllocationPreviewStatus;
  preview: AnnotationAgentAllocationPreviewResponse | null;
}

/**
 * The encounters to annotate (n) and, for the given humans, each one's share, from the
 * allocation preview. Nothing is requested while the agent is off.
 *
 * It is fetched again whenever a step using it mounts, because the files may have been
 * uploaded again on an earlier step; until the answer arrives the status is 'loading'.
 *
 * @param mode - The allocation mode; in 'custom' every human carries a `sharePercentage`.
 * @param humans - The human annotators in selection order; empty, in Default, to ask for n only.
 */
export const useAnnotationAgentAllocationPreview = (
  isAgentOn: boolean,
  mode: AnnotationAgentAllocationPreviewRequest['mode'],
  humans: AnnotationAgentAllocationPreviewRequest['humans']
): UseAnnotationAgentAllocationPreviewResult => {
  const workflowId = useAppSelector(selectWorkflowId);
  // RTK Query keys the request by its serialized arguments, so a new array with the same IDs
  // is the same request.
  const { currentData, isError, isFetching } = useGetAnnotationAgentAllocationPreviewQuery(
    isAgentOn && workflowId ? { workflowId, mode, humans } : skipToken,
    { refetchOnMountOrArgChange: true }
  );

  if (isFetching || (!currentData && !isError)) return { status: 'loading', preview: null };
  if (isError || !currentData) return { status: 'error', preview: null };
  return { status: 'ready', preview: currentData.data };
};
